import { TZDateMini } from '@date-fns/tz';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
  FormEventHandler as ReactFormEventHandler,
  FocusEventHandler,
  ClipboardEventHandler,
  KeyboardEventHandler as ReactKeyboardEventHandler,
} from 'react';

import { _useAriaLabelingProps as useAriaLabelingProps } from '@dynatrace/strato-components/core';
import {
  parseTimeAsTimeValue,
  _parseTimeExpression,
} from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useDateTimeInputValidation } from './useDateTimeIInputValidation.js';
import { useDateTimeParser } from './useDateTimeParser.js';
import { useControllableState } from '../../../core/hooks/useControllableState.js';
import type { TimeValue } from '../../../core/types/time.js';
import { useDateTimePicker } from '../../date-time-picker/hooks/useDateTimePicker.js';
import {
  getTimeValue,
  formatTimeValue,
} from '../../forms-core/time/timeParser.js';
import { useSelection } from '../../spin-buttons/hooks/useSelection.js';
import type { NumberSpinButtonRef } from '../../spin-buttons/number-spin-button/types.js';
import type { SpinButtonHandler } from '../../spin-buttons/spin-button-group/types.js';
import { isEqualTimeValue } from '../helpers.js';
import type { DateTimeInputProps } from '../types.js';

function isNumberSpinButton(
  handler: SpinButtonHandler | undefined,
): handler is NumberSpinButtonRef {
  return handler !== undefined && handler.type === 'number';
}

/*
 * A hook that provides the default functionality for the DateTimeInput picker
 */
export const useDateTimeInput = (
  props: DateTimeInputProps,
  expressionInputRef: RefObject<HTMLSpanElement | null>,
) => {
  const {
    precision,
    disabled,
    readOnly,
    required,
    onError,
    min,
    max,
    type,
    onChange,
  }: DateTimeInputProps = props;

  const ariaProps = useAriaLabelingProps(props);
  const { regexDateTimeStart, maxValue, types } = useDateTimeParser({
    type,
    precision,
  });
  // ? INVESTIGATE: without the generic, building the visreg story viewer and unit-test typechecking fails,
  // ? but the components-preview:build target still succeeds without any issues -> why?
  const [value, setValue] = useControllableState<TimeValue | null>(props);
  const isControlled = props.value !== undefined;
  const [valueType, setValueType] = useState(value?.type ?? 'expression');
  const firstRun = useRef(true);
  const [expressionEmpty, setExpressionEmpty] = useState(
    !expressionInputRef.current?.textContent,
  );
  const [inputValue, setInputValue] = useState('');

  const {
    spinButtons,
    parts,
    toDateTime,
    handlers: spinButtonHandlers,
    empty: spinButtonsEmpty,
    applyDateTime,
    firstSpinButtonHandler,
  } = useDateTimePicker({
    type,
    precision,
    disabled,
    readOnly,
    valueType,
    value:
      // only apply the absolute date if the input mode was already the datetimepicker
      value?.type === 'iso8601' && valueType === 'iso8601'
        ? value.absoluteDate
        : null,
    onChange: (newIsoDateTime) => {
      const newValue = getTimeValue(newIsoDateTime ?? undefined);

      if (newIsoDateTime === '') {
        // Trigger the onChange passed to the DateTimePicker to let the user know
        // that the currently entered value is invalid (incomplete spin buttons input).
        onChange?.('');
        return;
      }

      if (newValue === undefined) {
        return;
      }

      // only update the value if the newTimeValue differs from the prev. one
      setValue((prevValue) =>
        isEqualTimeValue(prevValue, newValue) ? prevValue : newValue,
      );

      validateTimeValue(newValue);
    },
    ...ariaProps,
  });

  const empty = useMemo(
    () => (valueType === 'expression' ? expressionEmpty : spinButtonsEmpty),
    [valueType, expressionEmpty, spinButtonsEmpty],
  );

  // switch input mode based on controlled value change
  useEffect(() => {
    if (value?.type === 'iso8601' && !spinButtonsEmpty) {
      setInputValue(value.value);
    }

    // ignore if in uncontrolled mode
    if (!isControlled || typeof value === 'string') {
      return;
    }

    // reset input if value is null
    if (!value) {
      setValueType('expression');
      updateExpression(null);
      const input = expressionInputRef.current?.textContent;
      setExpressionEmpty(input === '');
      spinButtonHandlers.forEach((sb) => sb?.clearValue(true));
      validateTimeValue(value, input ?? '');
      return;
    }

    // apply value to expression
    // the spinbutton update is handled within the useSpinButton hook internally
    const newValueType = value.type ?? 'expression';
    setValueType(value.type);
    if (newValueType === 'expression') {
      updateExpression(value);
      setExpressionEmpty(value.value === '');
      validateTimeValue(value);
    } else {
      setInputValue(value.value);
    }
    // only react to outside value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // hook to evaluate the error state based on the given expression, value, and spinbutton state
  const { validateTimeValue } = useDateTimeInputValidation(
    {
      min,
      max,
      required,
      onError, // fired on update
    },
    parts,
    toDateTime,
  );

  useEffect(() => {
    // Ignore expression updates, only handle spinbutton update in here
    if (valueType === 'expression') {
      return;
    }
    validateTimeValue(value);
  }, [validateTimeValue, value, valueType]);

  const { selectAll: expressionInputSelectAll } =
    useSelection(expressionInputRef);

  // called on onBlur to sanitize the current expression
  const updateExpression = useCallback(
    (value: TimeValue | null) => {
      if (!expressionInputRef.current) {
        return;
      }
      const formattedValue = value ? formatTimeValue(value) : '';
      const currentValue = formatTimeValue(
        expressionInputRef.current?.textContent ?? '',
      );

      // only update if the current value differs from the new value
      // don't update the textnode while typing but allow e.g. pasting a new expression
      if (currentValue !== formattedValue) {
        expressionInputRef.current.textContent = formattedValue;
      }
    },
    // no use in adding the expressionInputRef to the dep. array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // handles input events on the expression contenteditable span
  const expressionInputHandler: ReactFormEventHandler<HTMLSpanElement> =
    useCallback(() => {
      if (!expressionInputRef.current) {
        return;
      }

      const input = expressionInputRef.current.textContent ?? '';

      // Remove <br> added by browser in contenteditable to ensure placeholder works
      if (expressionInputRef.current.innerHTML === '<br>') {
        expressionInputRef.current.innerHTML = '';
      }

      // fallback to expression if text content is removed
      if (input === '') {
        setValueType('expression');
        setValue(null);
        setExpressionEmpty(true);
        validateTimeValue(parseTimeAsTimeValue(input));
        return;
      }

      setExpressionEmpty(false);

      // if the input starts like a valid datetime for the current locale, switch to the datetime picker
      const [, carryOver] = regexDateTimeStart.exec(input) ?? [];
      if (Number.parseInt(carryOver) <= maxValue) {
        setValueType('iso8601');

        // clear all spinbuttons
        applyDateTime(null);

        // get a cleaned and sorted list of spinbutton handlers
        const sortedHandlers = types
          .filter((type) => type !== 'literal')
          .map((type) => spinButtonHandlers.get(type))
          .filter(Boolean) as SpinButtonHandler[];
        const [carryOverSB, focusSB] = sortedHandlers;

        // carry over entered value to the next number spinbutton
        isNumberSpinButton(carryOverSB) &&
          carryOverSB.applyText(carryOver, true);

        // clear expression input content
        expressionInputRef.current.textContent = '';

        // set focus after switch to expression mode happened
        requestAnimationFrame(() => {
          // set focus to second spinbutton
          focusSB?.element.current?.focus();
        });
        return;
      }

      // if input is a valid expression, update the internal value state
      const newValue = parseTimeAsTimeValue(input);

      // reevaluate if the new input is valid or not
      validateTimeValue(newValue, input);

      if (newValue === null && input !== '') {
        onChange?.('');
      }
      // only update the value if the newTimeValue differs from the prev. one
      setValue((prevValue) =>
        isEqualTimeValue(prevValue, newValue) ? prevValue : newValue,
      );

      setInputValue(input);
    }, [
      expressionInputRef,
      regexDateTimeStart,
      maxValue,
      validateTimeValue,
      setValue,
      applyDateTime,
      types,
      spinButtonHandlers,
      onChange,
    ]);

  // select expression text content on focus
  const expressionOnFocusHandler: FocusEventHandler<HTMLSpanElement> =
    useCallback(() => {
      expressionInputSelectAll();
    }, [expressionInputSelectAll]);

  // sanitize textnode if content is an expression
  const expressionOnBlurHandler: FocusEventHandler<HTMLSpanElement> =
    useCallback(() => {
      if (expressionInputRef.current && value?.type === 'expression') {
        const currentExpressionInput =
          expressionInputRef.current.textContent ?? '';
        const isValidExpression = _parseTimeExpression(
          currentExpressionInput.trim(),
        );

        if (isValidExpression) {
          expressionInputRef.current.textContent = formatTimeValue(value);
        }
      }

      // eslint annoyingly wants to add the ref to the dependency list
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

  // if the value is a valid TimeValue, copy the ISO or expression to the clipboard
  const onCopyCapture: ClipboardEventHandler<HTMLSpanElement> = useCallback(
    (event) => {
      if (value === null) {
        return;
      }

      navigator.clipboard.writeText(value.value);
      event.preventDefault();
      event.stopPropagation();
    },
    [value],
  );

  // clipboard paste handler. apply clipboard content if formatted correctly
  const onPaste: ClipboardEventHandler<HTMLSpanElement> = useCallback(
    (event) => {
      if (readOnly) {
        return;
      }

      const data = event.clipboardData?.getData('text/plain');
      if (!data) {
        return;
      }

      // if the clipboard content is a valid expression or ISO, apply it as value
      const nextValue = parseTimeAsTimeValue(
        data.trim(),
        TZDateMini.tz(getTimezone()).getTime(),
        precision,
      );
      if (nextValue) {
        setValueType(nextValue.type);
        if (nextValue.type === 'expression') {
          updateExpression(nextValue);
        } else {
          // If the new value is switching the input mode, delay updating the spinbuttons by one render cycle
          requestAnimationFrame(() => applyDateTime(nextValue.absoluteDate));
        }
        setValue(nextValue);
        event.preventDefault();
      }
    },
    [applyDateTime, precision, readOnly, setValue, updateExpression],
  );

  // prevent unwanted input keys like ENTER
  const expressionOnKeyDownHandler: ReactKeyboardEventHandler<HTMLSpanElement> =
    useCallback((event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    }, []);

  // focus the first spinbutton or the expression input based on the current input mode
  const focus = useCallback(() => {
    switch (valueType) {
      case 'expression':
        expressionInputRef.current?.focus();
        break;
      case 'iso8601':
        firstSpinButtonHandler?.element?.current?.focus();
        break;
    }
    // no need to pass the refs as dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueType, firstSpinButtonHandler]);

  // first initialization of the uncontrolled expression value on component mount
  useEffect(() => {
    if (isControlled || value?.type !== 'expression') {
      return;
    }
    updateExpression(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if the spinbuttons are empty ...
  useEffect(() => {
    // Don't run the empty spinbutton check on component mount.
    if (firstRun.current) {
      firstRun.current = false;
      validateTimeValue(value);
      return;
    }

    // ... switch to expression input
    if (valueType === 'iso8601' && spinButtonsEmpty) {
      setValueType('expression');
      setExpressionEmpty(true);

      if (value !== null) {
        setValue(null);
      }

      setInputValue('');

      // reevaluate error state
      validateTimeValue(null);

      // wait until the expression input is visible to set the focus
      setTimeout(() => {
        expressionInputRef?.current?.focus();
      });
    }
    // only react on empty state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinButtonsEmpty]);

  useEffect(() => {
    if (valueType !== 'expression') {
      return;
    }

    if (empty) {
      setInputValue('');
      return;
    }

    if (expressionInputRef.current?.textContent) {
      setInputValue(expressionInputRef.current?.textContent); // fixes validating initial expression value in the DateTimeRangePicker
    }
    // valueType can be excluded because empty already depends on it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empty]);

  return {
    empty,
    spinButtons,
    spinButtonHandlers,
    value: typeof value === 'string' ? null : value,
    valueType,
    inputValue,
    expressionInputHandler,
    expressionOnFocusHandler,
    expressionOnBlurHandler,
    expressionOnKeyDownHandler,
    onCopyCapture,
    onPaste,
    focus,
    validateTimeValue,
  };
};
